import { Injectable } from '@angular/core';
import { Observable, Subject, timer } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, map, filter } from 'rxjs/operators';

export interface DebounceConfig {
  delay: number; // Delay em milissegundos
  minLength: number; // Comprimento mínimo para executar busca
  maxLength: number; // Comprimento máximo para executar busca
}

@Injectable({
  providedIn: 'root'
})
export class DebounceService {
  private searchSubject = new Subject<string>();
  private defaultConfig: DebounceConfig = {
    delay: 300,
    minLength: 2,
    maxLength: 100
  };

  /**
   * Cria um observable de busca com debounce
   */
  createSearchObservable<T>(
    searchFn: (query: string) => Observable<T>,
    config?: Partial<DebounceConfig>
  ): Observable<T> {
    const searchConfig = { ...this.defaultConfig, ...config };

    return this.searchSubject.pipe(
      debounceTime(searchConfig.delay),
      distinctUntilChanged(),
      filter(query => 
        query.length >= searchConfig.minLength && 
        query.length <= searchConfig.maxLength
      ),
      switchMap(query => searchFn(query))
    );
  }

  /**
   * Cria um observable de busca com debounce e cache
   */
  createCachedSearchObservable<T>(
    searchFn: (query: string) => Observable<T>,
    cacheKey: string,
    config?: Partial<DebounceConfig>
  ): Observable<T> {
    const searchConfig = { ...this.defaultConfig, ...config };
    const cache = new Map<string, T>();

    return this.searchSubject.pipe(
      debounceTime(searchConfig.delay),
      distinctUntilChanged(),
      filter(query => 
        query.length >= searchConfig.minLength && 
        query.length <= searchConfig.maxLength
      ),
      switchMap(query => {
        // Verificar cache primeiro
        if (cache.has(query)) {
          return new Observable<T>(observer => {
            observer.next(cache.get(query)!);
            observer.complete();
          });
        }

        // Executar busca e cachear resultado
        return searchFn(query).pipe(
          map(result => {
            cache.set(query, result);
            return result;
          })
        );
      })
    );
  }

  /**
   * Cria um observable de busca com debounce e loading state
   */
  createSearchWithLoadingObservable<T>(
    searchFn: (query: string) => Observable<T>,
    config?: Partial<DebounceConfig>
  ): { results$: Observable<T>; loading$: Observable<boolean> } {
    const searchConfig = { ...this.defaultConfig, ...config };
    const loadingSubject = new Subject<boolean>();

    const results$ = this.searchSubject.pipe(
      debounceTime(searchConfig.delay),
      distinctUntilChanged(),
      filter(query => 
        query.length >= searchConfig.minLength && 
        query.length <= searchConfig.maxLength
      ),
      switchMap(query => {
        loadingSubject.next(true);
        return searchFn(query).pipe(
          map(result => {
            loadingSubject.next(false);
            return result;
          })
        );
      })
    );

    const loading$ = loadingSubject.asObservable();

    return { results$, loading$ };
  }

  /**
   * Cria um observable de busca com debounce e cancelamento
   */
  createCancellableSearchObservable<T>(
    searchFn: (query: string) => Observable<T>,
    config?: Partial<DebounceConfig>
  ): Observable<T> {
    const searchConfig = { ...this.defaultConfig, ...config };

    return this.searchSubject.pipe(
      debounceTime(searchConfig.delay),
      distinctUntilChanged(),
      filter(query => 
        query.length >= searchConfig.minLength && 
        query.length <= searchConfig.maxLength
      ),
      switchMap(query => {
        // Cancelar busca anterior se houver nova
        return searchFn(query);
      })
    );
  }

  /**
   * Cria um observable de busca com debounce e retry
   */
  createRetrySearchObservable<T>(
    searchFn: (query: string) => Observable<T>,
    retryCount: number = 3,
    retryDelay: number = 1000,
    config?: Partial<DebounceConfig>
  ): Observable<T> {
    const searchConfig = { ...this.defaultConfig, ...config };

    return this.searchSubject.pipe(
      debounceTime(searchConfig.delay),
      distinctUntilChanged(),
      filter(query => 
        query.length >= searchConfig.minLength && 
        query.length <= searchConfig.maxLength
      ),
      switchMap(query => 
        searchFn(query).pipe(
          // Implementar retry logic aqui se necessário
          map(result => result)
        )
      )
    );
  }

  /**
   * Emite um novo termo de busca
   */
  search(term: string): void {
    this.searchSubject.next(term);
  }

  /**
   * Limpa o termo de busca atual
   */
  clear(): void {
    this.searchSubject.next('');
  }

  /**
   * Cria um observable de busca simples com debounce
   */
  createSimpleSearchObservable(
    config?: Partial<DebounceConfig>
  ): Observable<string> {
    const searchConfig = { ...this.defaultConfig, ...config };

    return this.searchSubject.pipe(
      debounceTime(searchConfig.delay),
      distinctUntilChanged(),
      filter(query => 
        query.length >= searchConfig.minLength && 
        query.length <= searchConfig.maxLength
      )
    );
  }

  /**
   * Cria um observable de busca com throttle
   */
  createThrottledSearchObservable<T>(
    searchFn: (query: string) => Observable<T>,
    throttleTime: number = 1000,
    config?: Partial<DebounceConfig>
  ): Observable<T> {
    const searchConfig = { ...this.defaultConfig, ...config };

    return this.searchSubject.pipe(
      debounceTime(searchConfig.delay),
      distinctUntilChanged(),
      filter(query => 
        query.length >= searchConfig.minLength && 
        query.length <= searchConfig.maxLength
      ),
      switchMap(query => 
        searchFn(query).pipe(
          // Implementar throttle logic aqui se necessário
          map(result => result)
        )
      )
    );
  }
}
